import React, { useEffect, useState } from 'react';
import { Box, Typography, Skeleton, Card, CardContent, Stack } from '@mui/material';
import { ResponsiveContainer, PieChart, Pie, Cell, Tooltip } from 'recharts';


import { MatchingOverviewData } from '../../../../types/Matching';

interface MatchingHeaderProps {
    overview: MatchingOverviewData | null;
}

// Color mapping for matched vs unmatched mentees
const COLORS = {
    matched: '#4caf50',   // Green for matched
    unmatched: '#f44336'  // Red for unmatched
};

const MatchingHeader: React.FC<MatchingHeaderProps> = ({ overview }) => {
    const [chartData, setChartData] = useState<Array<{ id: number; value: number; name: string; color: string }>>([]);
    const [loading, setLoading] = useState<boolean>(true);


    useEffect(() => {
        if (!overview) {
            setLoading(true);
            return;
        }
        const totalMentees = overview.mentees.length;
        const matchedCount = overview.mentees.filter((m) => m.matched).length;
        const unmatchedCount = totalMentees - matchedCount;

        const data = [
            { id: 0, value: matchedCount, name: 'Matched', color: COLORS.matched },
            { id: 1, value: unmatchedCount, name: 'Unmatched', color: COLORS.unmatched }
        ].filter((d) => d.value > 0);

        setChartData(data);

        setLoading(false);
    }, [overview]);

    const content = () => {
        if (loading) {
            return <Skeleton variant="circular" width={150} height={150} />;
        }

        if (chartData.length === 0) {
            return (
                <Typography variant="body2" color="text.secondary">
                    No matching data available yet
                </Typography>
            );
        }
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                <ResponsiveContainer width={200} height={200}>
                <PieChart>
                    <Pie
                        dataKey="value"
                        data={chartData}
                        cx="50%"
                        cy="50%"
                        outerRadius={80}
                        labelLine={false}
                    >
                        {chartData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                    </Pie>
                    <Tooltip />
                </PieChart>
                </ResponsiveContainer>
            </Box>
        );
    };

    return (
        <Card sx={{ width: '100%', p: 2, mb: 2 }}>
        <CardContent sx={{ textAlign: 'center' }}>
            <Typography variant="h6" gutterBottom>
                Matching Overview
            </Typography>
            {content()}
            {/* Legend */}
            {overview && (
                <Stack direction="row" spacing={4} justifyContent="center" sx={{ mt: 2 }}>
                    <Stack direction="row" spacing={1} alignItems="center">
                        <Box sx={{ width: 14, height: 14, bgcolor: COLORS.matched, borderRadius: '2px' }} />
                        <Typography variant="body2" sx={{ color: COLORS.matched }}>
                            Matched: {overview.mentees.filter(m => m.matched).length}
                        </Typography>
                    </Stack>
                    <Stack direction="row" spacing={1} alignItems="center">
                        <Box sx={{ width: 14, height: 14, bgcolor: COLORS.unmatched, borderRadius: '2px' }} />
                        <Typography variant="body2" sx={{ color: COLORS.unmatched }}>
                            Unmatched: {overview.mentees.filter(m => !m.matched).length}
                        </Typography>
                    </Stack>
                </Stack>
            )}
        </CardContent>
    </Card>
    );
};

export default MatchingHeader;
